import { env } from '@/config/env';
import { logPrLaneResponseHeaders, prLaneHeaders } from '@/lib/pr-lane';

export interface RequestAuth {
  token: string | null;
  orgId: string | null;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Calls rivendell with the OptiID auth headers attached. Every request forwards
 * `X-OptiID-Token` (the OptiID JWT) and `X-OptiID-Org-Id` (active Turnstile org),
 * per OCP-2056 / design doc 2.9. When a PR lane is active, the `ocp-pr-lane` header
 * is added too so the request is routed to that ephemeral environment.
 */
export async function apiFetch<T>(
  path: string,
  auth: RequestAuth,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (auth.token) headers.set('X-OptiID-Token', auth.token);
  if (auth.orgId) headers.set('X-OptiID-Org-Id', auth.orgId);
  for (const [key, value] of Object.entries(prLaneHeaders())) {
    headers.set(key, value);
  }

  const response = await fetch(`${env.rivendellBaseUrl}${path}`, { ...init, headers });
  logPrLaneResponseHeaders(response);

  if (!response.ok) {
    throw new ApiError(response.status, `Request to ${path} failed with ${response.status}`);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}
